import React, { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { churchAPI } from '../lib/api';
import { BommCard } from '../components/BommCard';
import { InviteQrCard } from '../components/InviteQrCard';
import { LiveDataCard } from '../components/LiveDataCard';
import { TopBar } from '../components/layout/TopBar';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Settings, Loader2, Save, Church, Mail, MapPin } from 'lucide-react';
import { toast } from 'sonner';

export default function SettingsPage() {
  const { selectedChurchId, canUpdate } = useAuth();
  const { setMobileMenuOpen, churchInfo } = useOutletContext();
  const [church, setChurch] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    name: '', physical_address: '', email: '', phone: '', website: '',
    ministry_denomination: '', schedules: '', dogmatic_description: ''
  });

  const fetchChurch = () => {
    if (!selectedChurchId) { setLoading(false); return; }
    setLoading(true);
    churchAPI.get(selectedChurchId)
      .then(res => {
        const data = res.data?.data || {};
        setChurch(data);
        setForm({
          name: data.name || '',
          physical_address: data.physical_address || '',
          email: data.email || '',
          phone: data.phone || '',
          website: data.website || '',
          ministry_denomination: data.ministry_denomination || '',
          schedules: data.schedules || '',
          dogmatic_description: data.dogmatic_description || '',
        });
      })
      .catch(() => toast.error('Error al cargar iglesia'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchChurch(); }, [selectedChurchId]);

  const handleSave = async () => {
    if (!selectedChurchId) return;
    setSaving(true);
    try {
      await churchAPI.update(selectedChurchId, form);
      toast.success('Configuración guardada');
    } catch { toast.error('Error al guardar'); } finally { setSaving(false); }
  };

  return (
    <div>
      <TopBar title="Configuración" onMenuClick={() => setMobileMenuOpen(true)} churchInfo={churchInfo} />
      <div className="px-4 sm:px-6 lg:px-8 py-5 max-w-3xl">
        {!selectedChurchId ? (
          <div className="empty-state"><Settings className="w-12 h-12 text-muted-foreground/40" /><p>Selecciona una iglesia para ver su configuración</p></div>
        ) : loading ? (
          <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 spinner text-[#056A84]" /></div>
        ) : (
          <div className="space-y-6">
            <Card>
              <CardHeader><CardTitle className="flex items-center gap-2"><Church className="w-5 h-5" /> Información de la Iglesia</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2"><Label>Nombre</Label><Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} data-testid="settings-name-input" /></div>
                <div className="space-y-2"><Label>Denominación</Label><Input value={form.ministry_denomination} onChange={e => setForm({...form, ministry_denomination: e.target.value})} /></div>
                <div className="space-y-2"><Label>Descripción dogmática</Label><Textarea value={form.dogmatic_description} onChange={e => setForm({...form, dogmatic_description: e.target.value})} rows={3} /></div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader><CardTitle className="flex items-center gap-2"><Mail className="w-5 h-5" /> Contacto</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2"><Label>Email</Label><Input value={form.email} onChange={e => setForm({...form, email: e.target.value})} data-testid="settings-email-input" /></div>
                  <div className="space-y-2"><Label>Teléfono</Label><Input value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} data-testid="settings-phone-input" /></div>
                </div>
                <div className="space-y-2"><Label>Sitio Web</Label><Input value={form.website} onChange={e => setForm({...form, website: e.target.value})} /></div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader><CardTitle className="flex items-center gap-2"><MapPin className="w-5 h-5" /> Ubicación</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2"><Label>Dirección física</Label><Input value={form.physical_address} onChange={e => setForm({...form, physical_address: e.target.value})} data-testid="settings-address-input" /></div>
                <div className="space-y-2"><Label>Horarios</Label><Textarea value={form.schedules} onChange={e => setForm({...form, schedules: e.target.value})} rows={2} /></div>
              </CardContent>
            </Card>

            <Button className="bg-[#056A84] hover:bg-[#045A70] text-white btn-lift" onClick={handleSave} disabled={saving} data-testid="settings-save-button">
              {saving ? <Loader2 className="w-4 h-4 spinner mr-1" /> : <Save className="w-4 h-4 mr-1" />}
              Guardar cambios
            </Button>

            <InviteQrCard />

            <LiveDataCard />

            <BommCard />
          </div>
        )}
      </div>
    </div>
  );
}
